import React, { Fragment } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Button } from 'reactstrap';
import { useAssessmentUIContext } from '../AssessmentUIProvider';
import AssessmentTableColumns, { AssessmentColumns } from './AssessmentTableColumns';
import { AssessmentTableSearchBar } from './AssessmentTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getAssessmentsList, setAssessmentFilters } from '../../redux/actions';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';
import EyeIcon from 'mdi-react/EyeIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import SettingsIcon from 'mdi-react/SettingsIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import BinIcon from 'mdi-react/BinIcon';

const AssessmentTable = () => {
  const assessmentUIContext = useAssessmentUIContext();
  const dispatch = useDispatch();
  const {
    data: assessments,
    error,
    filters,
    loading,
    totalRecords,
  } = useSelector((state) => state.assessments, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [popOver, setPopover] = React.useState({});

  React.useEffect(() => {
    dispatch(getAssessmentsList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setAssessmentFilters({ sortBy: property, isSortOrderDescending: !isSortOrderDescending }),
    );
  };

  const handleChangePage = (event, currentPage) => {
    dispatch(setAssessmentFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setAssessmentFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - assessments.length : 0;

  const renderActionButtons = ({ id: assessmentId }) => (
    <div className="actions">
      <Button
        className="icon mb-2"
        color="secondary"
        outline
        onClick={() => assessmentUIContext.openAssessmentConfigureDialog(assessmentId)}
      >
        <p>
          <SettingsIcon />
          Configure
        </p>
      </Button>
      <Button
        className="icon mb-2"
        color="secondary"
        outline
        onClick={() => assessmentUIContext.openAssessmentDuplicateDialog(assessmentId)}
      >
        <p>
          <ContentCopyIcon /> Duplicate
        </p>
      </Button>
      <Button
        className="icon mb-2"
        color="success"
        outline
        onClick={() => assessmentUIContext.openViewAssessmentDialog(assessmentId)}
      >
        <p>
          <EyeIcon /> View
        </p>
      </Button>
      <Button
        className="icon mb-2"
        color="primary"
        outline
        onClick={() => assessmentUIContext.openEditAssessmentDialog(assessmentId)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
      <Button
        className="icon mb-2"
        color="danger"
        outline
        onClick={() => assessmentUIContext.openDeleteAssessmentDialog(assessmentId)}
      >
        <p>
          <BinIcon /> Delete
        </p>
      </Button>
    </div>
  );

  return (
    <Fragment>
      <div className="material-table__wrap">
        <AssessmentTableSearchBar />
        <Table className="material-table">
          <AssessmentTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={AssessmentColumns.length}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading assessments...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!assessments.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={AssessmentColumns.length} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No Assessments'
                    : 'No Assessments with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {assessments.map((assessment) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={assessment?.id}>
                  <TableCell
                    className="material-table__cell"
                    component="th"
                    align="center"
                    scope="row"
                  >
                    {assessment.title}
                  </TableCell>
                  <TableCell
                    className="material-table__cell"
                    component="th"
                    align="center"
                    scope="row"
                  >
                    {assessment.categoryName}
                  </TableCell>
                  <TableCell
                    className="material-table__cell"
                    component="th"
                    align="center"
                    scope="row"
                  >
                    {assessment.assessmentTypeName}
                  </TableCell>
                  <TableCell
                    className="material-table__cell"
                    component="th"
                    align="center"
                    scope="row"
                  >
                    {assessment.customerName}
                  </TableCell>

                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: '295px' }}
                  >
                    {renderActionButtons(assessment)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={AssessmentColumns.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default AssessmentTable;
